import { useCallback, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { TabView, TabPanel } from "primereact/tabview";
import { InputSwitch } from "primereact/inputswitch";
import { passengerAdmin, getAdminConfigWarning, asAdminList } from "../components/services/adminBackendFetch";
import { AdminPageHeader } from "../components/AdminPageHeader";
import { AdminTableShell } from "../components/AdminTableShell";
import { useAppContext } from "../providers/ProviderAppContainer";

/** Money / minute / flat fields — stored as-is. */
const FARE_FIELDS = [
    { key: "city_fees", label: "City fee (rider charge + driver deduction)", step: 0.01 },
    { key: "stop_flat_fee", label: "Stop charge (flat)", step: 0.01 },
    { key: "stop_waiting_per_minute", label: "Stop waiting / min", step: 0.01 },
    { key: "stop_free_waiting_minutes", label: "Included stop waiting (min)", step: 1, integer: true },
    { key: "trip_waiting_per_minute", label: "Waiting charge / min", step: 0.01 },
    { key: "distance_rate_per_100_meters", label: "Distance rate / 100m", step: 0.01 },
    { key: "other_charges_flat", label: "Other fees (flat)", step: 0.01 },
    { key: "cancellation_free_count", label: "Free cancellations", step: 1, integer: true },
    { key: "cancellation_fee_flat", label: "Cancellation charge", step: 0.01 },
    { key: "passenger_no_show_fee_flat", label: "No-show fee", step: 0.01 },
    { key: "pickup_wait_minutes", label: "Pickup wait (min)", step: 1 },
    { key: "stop_wait_timer_minutes", label: "Stop wait timer (min)", step: 1 },
];

/** Stored as 0–1 in API; shown/edited as whole-number percent 0–100. */
const PERCENT_FIELDS = [
    { key: "stop_surcharge_percent_of_base", label: "Stop surcharge % of base" },
    { key: "tax_percent", label: "Tax %" },
    { key: "other_charges_percent", label: "Other fees %" },
    { key: "default_driver_commission_rate", label: "Default driver commission %" },
    { key: "default_driver_tip_share_percent", label: "Driver tip share %" },
];

function rateToPercent(rate) {
    const n = Number(rate);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 10000) / 100;
}

function percentToRate(percent) {
    const n = Number(percent);
    if (!Number.isFinite(n)) return 0;
    return Math.round((n / 100) * 10000) / 10000;
}

function fareToForm(apiFare) {
    const form = { ...(apiFare || {}) };
    for (const f of PERCENT_FIELDS) {
        form[f.key] = rateToPercent(apiFare?.[f.key]);
    }
    return form;
}

function formToFarePayload(form) {
    const payload = { ...form };
    for (const f of PERCENT_FIELDS) {
        payload[f.key] = percentToRate(form[f.key]);
    }
    return payload;
}

const emptyVehicleForm = () => ({
    name: "",
    type: "Car",
    capacity: 4,
    base_fare: 12,
    icon: "",
    speed_kmh: 30,
});

export default function PricingPage() {
    const { showToast } = useAppContext();
    const configWarning = getAdminConfigWarning();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [fare, setFare] = useState({});
    const [appConfig, setAppConfig] = useState({
        currency: "cad",
        default_location: { latitude: 43.6532, longitude: -79.3832, location_name: "Toronto, ON", country: "CA" },
        add_stops_enabled: true,
        noOfStops: 2,
    });
    const [vehicles, setVehicles] = useState([]);
    const [vehicleEdits, setVehicleEdits] = useState({});
    const [newVehicle, setNewVehicle] = useState(emptyVehicleForm());

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [fareData, vehicleData, configData] = await Promise.all([
                passengerAdmin("/admin/fare-pricing"),
                passengerAdmin("/admin/vehicles"),
                passengerAdmin("/admin/app-config"),
            ]);
            setFare(fareToForm(fareData ?? {}));
            setAppConfig({
                currency: configData?.currency || "cad",
                maintenance_mode: configData?.maintenance_mode,
                firebase_key: configData?.firebase_key,
                default_location: configData?.default_location,
                add_stops_enabled:
                    configData?.add_stops_enabled !== undefined
                        ? Boolean(configData.add_stops_enabled)
                        : true,
                noOfStops: Number(configData?.noOfStops ?? configData?.no_of_stops ?? 2) || 0,
            });
            setVehicles(asAdminList(vehicleData));
            const edits = {};
            for (const v of asAdminList(vehicleData)) {
                edits[v.id] = {
                    name: v.name || "",
                    type: v.type || "Car",
                    base_fare: Number(v.base_fare) || 0,
                    capacity: Number(v.capacity) || 1,
                    icon: v.icon || "",
                    speed_kmh: Number(v.speed_kmh) || Number(fareData?.vehicle_speed_kmh?.[String(v.id)]) || 30,
                };
            }
            setVehicleEdits(edits);
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function saveFare() {
        setSaving(true);
        setError(null);
        try {
            await passengerAdmin("/admin/fare-pricing", {
                method: "PATCH",
                body: JSON.stringify(formToFarePayload(fare)),
            });
            showToast?.({ severity: "success", summary: "Saved", detail: "Fare settings updated." });
            await load();
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setSaving(false);
        }
    }

    async function saveAppConfig() {
        setSaving(true);
        setError(null);
        try {
            await passengerAdmin("/admin/app-config", {
                method: "PATCH",
                body: JSON.stringify({
                    currency: appConfig.currency,
                    default_location: appConfig.default_location,
                    maintenance_mode: appConfig.maintenance_mode,
                    add_stops_enabled: Boolean(appConfig.add_stops_enabled),
                    noOfStops: Math.max(0, Math.min(20, Number(appConfig.noOfStops) || 0)),
                }),
            });
            showToast?.({ severity: "success", summary: "Saved", detail: "App config updated." });
            await load();
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setSaving(false);
        }
    }

    async function saveVehicle(vehicleId, event) {
        setSaving(true);
        setError(null);
        try {
            const edit = { ...(vehicleEdits[vehicleId] || {}) };
            // Currency inputs can lag React state until blur. Read the row the user just edited.
            const tr = event?.currentTarget?.closest?.("tr");
            if (tr) {
                const inputs = [...tr.querySelectorAll("input")];
                const num = (raw) => {
                    const n = Number(String(raw ?? "").replace(/[^0-9.]/g, ""));
                    return Number.isFinite(n) ? n : null;
                };
                if (inputs[0]) edit.name = inputs[0].value;
                if (inputs[1]) edit.type = inputs[1].value;
                const cap = num(inputs[2]?.value);
                if (cap != null) edit.capacity = cap;
                const base = num(inputs[3]?.value);
                if (base != null) edit.base_fare = Math.round(base * 100) / 100;
                const speed = num(inputs[4]?.value);
                if (speed != null && speed > 0) edit.speed_kmh = speed;
                if (inputs[5]) edit.icon = inputs[5].value;
            }
            const baseFare = Number(edit.base_fare);
            if (!Number.isFinite(baseFare) || baseFare < 0) {
                throw new Error("Enter a valid base price (0 or greater) before saving.");
            }
            await passengerAdmin(`/admin/vehicles/${vehicleId}`, {
                method: "PATCH",
                body: JSON.stringify({
                    name: String(edit.name || "").trim(),
                    type: String(edit.type || "Car").trim() || "Car",
                    base_fare: Math.round(baseFare * 100) / 100,
                    capacity: edit.capacity,
                    icon: String(edit.icon || "").trim() || null,
                    speed_kmh: Number(edit.speed_kmh) || 30,
                }),
            });
            showToast?.({
                severity: "success",
                summary: "Saved",
                detail: `Base price $${baseFare.toFixed(2)} saved for this vehicle type.`,
            });
            await load();
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setSaving(false);
        }
    }

    async function deleteVehicle(vehicleId, vehicleName) {
        const label = vehicleName || `#${vehicleId}`;
        if (!window.confirm(`Delete vehicle type "${label}"? This cannot be undone.`)) return;
        setSaving(true);
        setError(null);
        try {
            await passengerAdmin(`/admin/vehicles/${vehicleId}`, { method: "DELETE" });
            showToast?.({ severity: "success", summary: "Deleted", detail: "Vehicle type removed." });
            await load();
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setSaving(false);
        }
    }

    async function createVehicle() {
        const name = String(newVehicle.name || "").trim();
        if (!name) {
            setError("Vehicle type name is required (e.g. SWYFT X).");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await passengerAdmin("/admin/vehicles", {
                method: "POST",
                body: JSON.stringify({
                    name,
                    type: String(newVehicle.type || "Car").trim() || "Car",
                    capacity: newVehicle.capacity,
                    base_fare: newVehicle.base_fare,
                    icon: String(newVehicle.icon || "").trim() || undefined,
                    speed_kmh: Number(newVehicle.speed_kmh) || 30,
                }),
            });
            showToast?.({
                severity: "success",
                summary: "Created",
                detail: `“${name}” saved to database and available on passenger/driver apps.`,
            });
            setNewVehicle(emptyVehicleForm());
            await load();
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setSaving(false);
        }
    }

    if (configWarning) {
        return <Message severity="warn" text={configWarning} className="w-full" />;
    }

    if (loading) {
        return (
            <div className="flex justify-content-center py-6">
                <ProgressSpinner />
            </div>
        );
    }

    const loc = appConfig.default_location || {};

    return (
        <div>
            <AdminPageHeader
                title="Settings"
                subtitle="SWYFT fare charges, vehicle types, and default map location."
            />
            {error ? <Message severity="error" text={error} className="w-full mb-3" /> : null}

            <TabView>
                <TabPanel header="Fare & charges">
                    <AdminTableShell header={<span className="font-semibold">Configurable fare fields</span>}>
                        <Message
                            severity="info"
                            className="w-full mb-3"
                            text="Per-vehicle starting price (base fare) is on the Vehicle types tab — not here. This tab controls city fee, tax, waiting, cancellation, and default driver commission."
                        />
                        <div className="grid">
                            {FARE_FIELDS.map((f) => (
                                <div key={f.key} className="col-12 md:col-4 mb-3">
                                    <label className="block text-sm font-medium mb-2">{f.label}</label>
                                    <InputNumber
                                        value={fare[f.key] ?? 0}
                                        onValueChange={(e) =>
                                            setFare((prev) => ({ ...prev, [f.key]: e.value ?? 0 }))
                                        }
                                        minFractionDigits={f.integer ? 0 : 2}
                                        maxFractionDigits={f.integer ? 0 : 4}
                                        step={f.step}
                                        className="w-full"
                                    />
                                </div>
                            ))}
                            {PERCENT_FIELDS.map((f) => (
                                <div key={f.key} className="col-12 md:col-4 mb-3">
                                    <label className="block text-sm font-medium mb-2">{f.label}</label>
                                    <InputNumber
                                        value={fare[f.key] ?? 0}
                                        onValueChange={(e) =>
                                            setFare((prev) => ({
                                                ...prev,
                                                [f.key]: Math.min(100, Math.max(0, e.value ?? 0)),
                                            }))
                                        }
                                        min={0}
                                        max={100}
                                        minFractionDigits={0}
                                        maxFractionDigits={2}
                                        suffix=" %"
                                        className="w-full"
                                    />
                                </div>
                            ))}
                        </div>
                        <Button label="Save fare settings" icon="pi pi-save" loading={saving} onClick={saveFare} />
                    </AdminTableShell>
                </TabPanel>

                <TabPanel header="Vehicle types">
                    <div className="mb-3">
                    <AdminTableShell
                        header={<span className="font-semibold">Add vehicle type (saved to database)</span>}
                    >
                        <div className="grid">
                            <div className="col-12 md:col-3 mb-3">
                                <label className="block text-sm font-medium mb-2">Name</label>
                                <InputText
                                    className="w-full"
                                    value={newVehicle.name}
                                    placeholder="SWYFT X"
                                    onChange={(e) => setNewVehicle((v) => ({ ...v, name: e.target.value }))}
                                />
                            </div>
                            <div className="col-12 md:col-2 mb-3">
                                <label className="block text-sm font-medium mb-2">Type</label>
                                <InputText
                                    className="w-full"
                                    value={newVehicle.type}
                                    placeholder="Car"
                                    onChange={(e) => setNewVehicle((v) => ({ ...v, type: e.target.value }))}
                                />
                            </div>
                            <div className="col-12 md:col-2 mb-3">
                                <label className="block text-sm font-medium mb-2">Capacity</label>
                                <InputNumber
                                    className="w-full"
                                    value={newVehicle.capacity}
                                    onValueChange={(e) =>
                                        setNewVehicle((v) => ({ ...v, capacity: e.value ?? 4 }))
                                    }
                                    min={1}
                                    max={20}
                                />
                            </div>
                            <div className="col-12 md:col-2 mb-3">
                                <label className="block text-sm font-medium mb-2">Base price (CAD)</label>
                                <InputNumber
                                    className="w-full"
                                    value={newVehicle.base_fare}
                                    onValueChange={(e) =>
                                        setNewVehicle((v) => ({ ...v, base_fare: e.value ?? 0 }))
                                    }
                                    min={0}
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    mode="decimal"
                                />
                            </div>
                            <div className="col-12 md:col-2 mb-3">
                                <label className="block text-sm font-medium mb-2">ETA speed (km/h)</label>
                                <InputNumber
                                    className="w-full"
                                    value={newVehicle.speed_kmh}
                                    onValueChange={(e) =>
                                        setNewVehicle((v) => ({ ...v, speed_kmh: e.value ?? 30 }))
                                    }
                                    min={1}
                                    max={200}
                                />
                            </div>
                            <div className="col-12 md:col-3 mb-3">
                                <label className="block text-sm font-medium mb-2">Icon filename / URL</label>
                                <InputText
                                    className="w-full"
                                    value={newVehicle.icon}
                                    placeholder="swyftx.png"
                                    onChange={(e) => setNewVehicle((v) => ({ ...v, icon: e.target.value }))}
                                />
                            </div>
                            <div className="col-12 md:col-2 mb-3 flex align-items-end">
                                <Button
                                    label="Add type"
                                    icon="pi pi-plus"
                                    className="w-full"
                                    loading={saving}
                                    onClick={createVehicle}
                                />
                            </div>
                        </div>
                        <div className="text-xs text-500 px-1 pb-2">
                            New types are written to <code>VECHILE_TYPES</code> and appear on passenger{" "}
                            <code>GET /vehicles</code>, <code>GET /configs</code>, and driver{" "}
                            <code>GET /vehicles</code>.
                        </div>
                    </AdminTableShell>
                    </div>

                    <AdminTableShell header={<span className="font-semibold">Existing vehicle types</span>}>
                        <Message
                            severity="warn"
                            className="w-full mb-3"
                            text="After changing Base price, click Save on that row (✓). The Fare & charges Save button does not update vehicle base prices."
                        />
                        <DataTable value={vehicles} size="small" stripedRows emptyMessage="No vehicle types yet.">
                            <Column field="id" header="ID" style={{ width: "4rem" }} />
                            <Column
                                header="Name"
                                body={(row) => (
                                    <InputText
                                        value={vehicleEdits[row.id]?.name ?? ""}
                                        onChange={(e) =>
                                            setVehicleEdits((prev) => ({
                                                ...prev,
                                                [row.id]: { ...prev[row.id], name: e.target.value },
                                            }))
                                        }
                                    />
                                )}
                            />
                            <Column
                                header="Type"
                                body={(row) => (
                                    <InputText
                                        value={vehicleEdits[row.id]?.type ?? "Car"}
                                        onChange={(e) =>
                                            setVehicleEdits((prev) => ({
                                                ...prev,
                                                [row.id]: { ...prev[row.id], type: e.target.value },
                                            }))
                                        }
                                    />
                                )}
                            />
                            <Column
                                header="Capacity"
                                body={(row) => (
                                    <InputNumber
                                        value={vehicleEdits[row.id]?.capacity ?? 1}
                                        onValueChange={(e) =>
                                            setVehicleEdits((prev) => ({
                                                ...prev,
                                                [row.id]: { ...prev[row.id], capacity: e.value ?? 1 },
                                            }))
                                        }
                                        min={1}
                                        max={20}
                                    />
                                )}
                            />
                            <Column
                                header="Base price"
                                body={(row) => (
                                    <InputNumber
                                        value={vehicleEdits[row.id]?.base_fare ?? 0}
                                        onValueChange={(e) =>
                                            setVehicleEdits((prev) => ({
                                                ...prev,
                                                [row.id]: { ...prev[row.id], base_fare: e.value ?? 0 },
                                            }))
                                        }
                                        min={0}
                                        minFractionDigits={2}
                                        maxFractionDigits={2}
                                        mode="decimal"
                                    />
                                )}
                            />
                            <Column
                                header="Speed"
                                body={(row) => (
                                    <InputNumber
                                        value={vehicleEdits[row.id]?.speed_kmh ?? 30}
                                        onValueChange={(e) =>
                                            setVehicleEdits((prev) => ({
                                                ...prev,
                                                [row.id]: { ...prev[row.id], speed_kmh: e.value ?? 30 },
                                            }))
                                        }
                                        min={1}
                                        max={200}
                                        suffix=" km/h"
                                    />
                                )}
                            />
                            <Column
                                header="Icon"
                                body={(row) => (
                                    <InputText
                                        value={vehicleEdits[row.id]?.icon ?? ""}
                                        onChange={(e) =>
                                            setVehicleEdits((prev) => ({
                                                ...prev,
                                                [row.id]: { ...prev[row.id], icon: e.target.value },
                                            }))
                                        }
                                    />
                                )}
                            />
                            <Column
                                header="Save"
                                body={(row) => (
                                    <div className="flex gap-2">
                                        <Button
                                            icon="pi pi-save"
                                            label="Save"
                                            className="p-button-sm"
                                            onClick={(e) => saveVehicle(row.id, e)}
                                            disabled={saving}
                                            aria-label="Save vehicle"
                                        />
                                        <Button
                                            icon="pi pi-trash"
                                            className="p-button-sm p-button-danger p-button-outlined"
                                            onClick={() => deleteVehicle(row.id, row.name)}
                                            disabled={saving}
                                            aria-label="Delete vehicle"
                                        />
                                    </div>
                                )}
                            />
                        </DataTable>
                    </AdminTableShell>
                </TabPanel>

                <TabPanel header="App config">
                    <AdminTableShell header={<span className="font-semibold">Passenger app — stops & default map</span>}>
                        <div className="grid">
                            <div className="col-12 md:col-6 mb-3">
                                <label className="block text-sm font-medium mb-2">Add Stops button (user app)</label>
                                <div className="flex align-items-center gap-3">
                                    <InputSwitch
                                        checked={Boolean(appConfig.add_stops_enabled)}
                                        onChange={(e) =>
                                            setAppConfig((c) => ({ ...c, add_stops_enabled: e.value }))
                                        }
                                    />
                                    <span className="text-sm text-600">
                                        {appConfig.add_stops_enabled ? "Enabled — show button" : "Disabled — hide button"}
                                    </span>
                                </div>
                                <div className="text-xs text-500 mt-1">
                                    Passenger <code>GET /configs</code> returns <code>add_stops_enabled</code> (true/false).
                                </div>
                            </div>
                            <div className="col-12 md:col-6 mb-3">
                                <label className="block text-sm font-medium mb-2">Max mid-stops (noOfStops)</label>
                                <InputNumber
                                    className="w-full"
                                    value={appConfig.noOfStops ?? 0}
                                    onValueChange={(e) =>
                                        setAppConfig((c) => ({
                                            ...c,
                                            noOfStops: Math.max(0, Math.min(20, e.value ?? 0)),
                                        }))
                                    }
                                    min={0}
                                    max={20}
                                    showButtons
                                />
                                <div className="text-xs text-500 mt-1">
                                    Max stops a user can add between pickup and destination. Returned as{" "}
                                    <code>noOfStops</code>.
                                </div>
                            </div>
                            <div className="col-12 md:col-6 mb-3">
                                <label className="block text-sm font-medium mb-2">Location name</label>
                                <InputText
                                    className="w-full"
                                    value={loc.location_name || ""}
                                    onChange={(e) =>
                                        setAppConfig((c) => ({
                                            ...c,
                                            default_location: {
                                                ...(c.default_location || {}),
                                                location_name: e.target.value,
                                            },
                                        }))
                                    }
                                />
                            </div>
                            <div className="col-12 md:col-3 mb-3">
                                <label className="block text-sm font-medium mb-2">Country</label>
                                <InputText
                                    className="w-full"
                                    value={loc.country || ""}
                                    onChange={(e) =>
                                        setAppConfig((c) => ({
                                            ...c,
                                            default_location: {
                                                ...(c.default_location || {}),
                                                country: e.target.value,
                                            },
                                        }))
                                    }
                                />
                            </div>
                            <div className="col-12 md:col-3 mb-3">
                                <label className="block text-sm font-medium mb-2">Currency</label>
                                <InputText
                                    className="w-full"
                                    value={appConfig.currency || "cad"}
                                    onChange={(e) =>
                                        setAppConfig((c) => ({ ...c, currency: e.target.value.toLowerCase() }))
                                    }
                                />
                            </div>
                            <div className="col-12 md:col-6 mb-3">
                                <label className="block text-sm font-medium mb-2">Latitude</label>
                                <InputNumber
                                    className="w-full"
                                    value={loc.latitude}
                                    onValueChange={(e) =>
                                        setAppConfig((c) => ({
                                            ...c,
                                            default_location: {
                                                ...(c.default_location || {}),
                                                latitude: e.value,
                                            },
                                        }))
                                    }
                                    minFractionDigits={4}
                                    maxFractionDigits={7}
                                />
                            </div>
                            <div className="col-12 md:col-6 mb-3">
                                <label className="block text-sm font-medium mb-2">Longitude</label>
                                <InputNumber
                                    className="w-full"
                                    value={loc.longitude}
                                    onValueChange={(e) =>
                                        setAppConfig((c) => ({
                                            ...c,
                                            default_location: {
                                                ...(c.default_location || {}),
                                                longitude: e.value,
                                            },
                                        }))
                                    }
                                    minFractionDigits={4}
                                    maxFractionDigits={7}
                                />
                            </div>
                        </div>
                        <Button
                            label="Save app config"
                            icon="pi pi-save"
                            loading={saving}
                            onClick={saveAppConfig}
                        />
                    </AdminTableShell>
                </TabPanel>
            </TabView>
        </div>
    );
}
